import React from "react";
import { createPortal } from "react-dom";
import { IconButton, Stack, Typography } from "@mui/material";
import { XIcon } from "src/ui/components/shared/atoms/icons/XIcon";
import { DownloadProgressBar } from "src/ui/components/shared/molecules/DownloadProgressBar";
import {
  DownloadJob,
  getDownloadJobs,
  subscribeDownloadJobs,
} from "src/util/downloadProgress";

type UseSyncExternalStore = <T>(
  subscribe: (onStoreChange: () => void) => () => void,
  getSnapshot: () => T,
  getServerSnapshot: () => T
) => T;

const useSyncExternalStore = (
  React as unknown as { useSyncExternalStore: UseSyncExternalStore }
).useSyncExternalStore;

const DownloadProgressCard = React.memo(({ job }: { job: DownloadJob }) => {
  return (
    <Stack
      spacing={0.5}
      role="status"
      aria-live="polite"
      sx={(theme) => ({
        position: "relative",
        width: 340,
        p: 4,
        pr: 6,
        minWidth: 0,
        bgcolor: theme.newColors.neutral.white,
        border: `1px solid ${theme.newColors.neutral.gray200}`,
        borderRadius: theme.spacing(2),
        boxShadow: "0 8px 24px rgba(39, 41, 55, 0.12)",
      })}
    >
      <IconButton
        aria-label="Cancel download"
        onClick={job.cancel}
        sx={(theme) => ({
          position: "absolute",
          top: 12,
          right: 12,
          p: 0.5,
          color: theme.newColors.neutral.gray500,
          "&:hover": {
            background: "none",
            color: theme.newColors.neutral.primary,
          },
        })}
      >
        <XIcon sx={{ width: 16, height: 16 }} />
      </IconButton>
      <Typography
        variant="subtitle2"
        sx={(theme) => ({ color: theme.newColors.neutral.primary })}
      >
        Downloading document
      </Typography>
      <Typography
        variant="body2"
        noWrap
        title={job.filename}
        sx={(theme) => ({ color: theme.newColors.neutral.gray500 })}
      >
        {job.filename}
      </Typography>
      <DownloadProgressBar progress={job.progress} />
    </Stack>
  );
});

export const DownloadProgressToast = () => {
  const jobs = useSyncExternalStore(
    subscribeDownloadJobs,
    getDownloadJobs,
    getDownloadJobs
  );

  if (jobs.length === 0 || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <Stack
      spacing={1.5}
      sx={{
        position: "fixed",
        right: 24,
        bottom: 24,
        zIndex: 2000,
        alignItems: "flex-end",
      }}
    >
      {jobs.map((job) => (
        <DownloadProgressCard key={job.id} job={job} />
      ))}
    </Stack>,
    document.body
  );
};
